import { Alert, AlertIcon, Box, Center, Flex, Spinner, Text, VStack } from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import { useApp } from '@hb/react'
import { Encounter, ENCOUNTERS, EncounterStatus, FieldMapValue } from '@hb/shared'
import { useMemo } from 'react'
import { useDocument } from '../../../backend'
import { formatEncounterDate } from '../utils'
import { getShowEncounterAmendments } from './amendmentHistory'
import { CdiSummary } from './CdiSummary'
import { EncounterAmendmentsSection } from './EncounterAmendmentsSection'
import { EncounterClaimsSection } from './EncounterClaims'
import { EncounterViewFooter } from './EncounterViewFooter'
import { EncounterViewHeader } from './EncounterViewHeader'
import { generateAllSections, renderSection, SectionWrapper } from './renderEncounterSection'
import { EncounterQuickAuthView } from './EncounterQuickAuthView'
import { PatientStatementsView } from './PatientStatementsView'

export { generateAllSections } from './renderEncounterSection'

export const EncounterView = ({
  examId,
  encounterOverride,
  patientId,
  onClose,
  onEdit,
}: {
  examId: string
  encounterOverride?: Encounter
  patientId: string
  onClose: () => void
  // For authenticated encounters the header passes the amendment reason back.
  onEdit?: (amendmentReason?: string) => void
}) => {
  const { appName } = useApp()
  const { encounters } = useProfile()
  const { dataArr: encountersArr } = encounters

  const { data: fetchedEncounter, loading } = useDocument<Encounter>(ENCOUNTERS, examId)
  const encounter = encounterOverride ?? fetchedEncounter

  const sections = useMemo(() => {
    if (!encounter) return []
    return generateAllSections(encounter, encountersArr ?? null)
  }, [encounter, encountersArr])

  const encounterData = useMemo(() => (encounter?.data ?? {}) as FieldMapValue, [encounter])

  if (!encounter && loading) {
    return (
      <Center w="100%" h="100%">
        <Spinner />
      </Center>
    )
  }

  if (!encounter) {
    return (
      <Center w="100%" h="100%">
        <Text color="gray.500">Encounter not found.</Text>
      </Center>
    )
  }

  // §1.6.1: the amendment record renders ahead of the clinical content — anyone reading
  // the note hits it first. Unamended notes render no amendment chrome.
  const showAmendments = getShowEncounterAmendments(encounter)
  const claimsIndex = showAmendments ? 1 : 0
  const sectionsOffset = claimsIndex + (appName === 'app' ? 1 : 0)

  return (
    <Flex
      flexFlow="column"
      w="100%"
      h="100%"
      bg="white"
      _dark={{ bg: 'gray.900' }}
      borderRadius={4}
      boxShadow="1px 1px 4px #00000077">
      <EncounterViewHeader encounter={encounter} onClose={onClose} onEdit={onEdit} />
      {/* §1.6 Quick Authenticate: show compact summary for COMPLETED (unauthenticated) encounters
          when the parent supports switching to edit mode. Full sections remain accessible via
          the header's Edit button or by opening the full form. */}
      {encounter.status === EncounterStatus.COMPLETED && onEdit ? (
        <Box flex={1} minH={0} w="100%" position="relative">
          <EncounterQuickAuthView encounter={encounter} onEdit={onEdit} />
        </Box>
      ) : (
        <Box flex={1} minH={0} overflowY="auto" w="100%">
          <Flex borderBottom="1px solid #cdcdcd" py={2} px={3} w="100%" align="center" gap={2}>
            <Text fontSize="sm" fontWeight={600} color="gray.600">
              Exam Date:
            </Text>
            <Text fontSize="sm">{encounter.date ? formatEncounterDate(encounter.date) : '—'}</Text>
          </Flex>
          {encounter.hasPatientStatement ? (
            <Alert status="warning" py={2} px={3} fontSize="xs" borderRadius={0}>
              <AlertIcon />
              Patient has submitted a written statement for this record.
            </Alert>
          ) : null}
          {/* §1.6.2: amber hold indicator — exact copy "Billing hold — [reason]"; renders in
              both apps (billing team sees it too). Display only — release is footer-link only. */}
          {encounter.currentBillingHold ? (
            <Alert status="warning" py={2} px={3} fontSize="xs" borderRadius={0}>
              <AlertIcon />
              Billing hold — {encounter.currentBillingHold.reason}
            </Alert>
          ) : null}
          <VStack w="100%" alignItems="flex-start" spacing={0}>
            {showAmendments ? (
              <SectionWrapper index={0} isLast={appName !== 'app' && sections.length === 0}>
                <EncounterAmendmentsSection
                  encounter={encounter}
                  encountersArr={encountersArr ?? null}
                />
              </SectionWrapper>
            ) : null}
            {appName === 'app' ? (
              <SectionWrapper index={claimsIndex} isLast={sections.length === 0}>
                <EncounterClaimsSection encounter={encounter} examId={examId} />
                {/* §6.7 "Billing team CDI view" — read-only; the interactive prompt lives
                    in the provider form (CdiPromptBanner). */}
                <CdiSummary encounter={encounter} />
              </SectionWrapper>
            ) : null}
            {sections.map((section, i) => {
              const content = renderSection(section, encounter, encounterData)
              if (content === null) return null
              return (
                <SectionWrapper
                  key={i}
                  index={i + sectionsOffset}
                  isLast={i === sections.length - 1}>
                  {content}
                </SectionWrapper>
              )
            })}
          </VStack>
          <Box px={4} pb={3}>
            <PatientStatementsView encounterId={examId} patientId={patientId} />
          </Box>
        </Box>
      )}
      <EncounterViewFooter encounter={encounter} encounterId={examId} />
    </Flex>
  )
}
